import React from 'react';
import { Link } from 'react-router-dom';
import { CircleStackIcon, PaperClipIcon } from '@heroicons/react/24/outline';

import { AnimateIn } from '@oysterjs/ui/transition';
import { MarkdownContainer, SanitizedMarkdown } from '@oysterjs/ais/components/sanitized-markdown';
import {
  AmsRefKind,
  type CaraWorkspaceFieldsFragment,
  KnowledgeGraphEntityType
} from '@oysterjs/ais/api/graphql';
import { entityTypeIcon } from '@oysterjs/ais/components/ams-picker/entity-display';
import { amsRefKey, entityTypeOfKind } from '@oysterjs/ais/components/ams-picker/ams-ref';
import { AmsReconciliationModal } from '@oysterjs/ais/components/workspaces/ams-reconciliation/ams-reconciliation-modal';

type MessageFile = CaraWorkspaceFieldsFragment['messages'][0]['files'][0];
type MessageAmsRefs = CaraWorkspaceFieldsFragment['messages'][0]['amsRefs'];

/** A pinned AMS record a sent message shows beside its files. */
type ContextPin = { key: string; entityType: KnowledgeGraphEntityType; displayName: string };

const CHIP_CLASS =
  'rounded-3xl bg-white dark:bg-neutral-800 shadow-sm border border-neutral-200 dark:border-neutral-700 p-2 flex flex-row gap-1.5 max-w-56';

export const UserMessage = (props: {
  messageId: string;
  content: string;
  attachments: MessageFile[];
  amsContext?: CaraWorkspaceFieldsFragment['messages'][0]['amsContext'];
  amsRefs?: MessageAmsRefs;
  /** Lets a file chip's reconciliation prefill the account linked to this workspace. */
  workspaceId?: string;
  /** Hides the file chips' reconcile action, as ResponseMessage hides its actions. */
  readOnly?: boolean;
  /**
   * Disables reconciliation while any attachment in the workspace is still being read,
   * since a file has no text to reconcile until then. The processing stage names files
   * but not their ids, so this cannot narrow to the one file.
   */
  attachmentsProcessing?: boolean;
  /** Resolved above the thread. The chip icon is only a reconcile action. */
  amsReconciliationEnabled?: boolean;
}) => {
  // A pinned file is one of the message's attachments already.
  const contextPins: ContextPin[] = [
    ...(props.amsContext ?? [])
      .filter((pin) => pin.entityType !== KnowledgeGraphEntityType.File)
      .map((pin) => ({
        key: pin.entityId,
        entityType: pin.entityType,
        displayName: pin.displayName
      })),
    ...(props.amsRefs ?? [])
      .filter((pin) => pin.ref.kind !== AmsRefKind.File)
      .map((pin) => ({
        key: amsRefKey(pin.ref),
        entityType: entityTypeOfKind(pin.ref.kind),
        displayName: pin.displayName
      }))
  ];
  const showAttachments = props.attachments.length > 0 || contextPins.length > 0;

  return (
    <AnimateIn
      className="flex flex-col items-end self-end"
      data-conversation-role="user-message"
      data-message-id={props.messageId}
    >
      <div className="px-5 py-2.5 max-w-xl rounded-3xl bg-primary-600 shadow text-white text-sm inline-flex">
        <MarkdownContainer className="prose-invert prose-p:text-white text-white">
          <SanitizedMarkdown content={props.content} />
        </MarkdownContainer>
      </div>
      {showAttachments && (
        <AttachmentsList
          attachments={props.attachments}
          contextPins={contextPins}
          workspaceId={props.workspaceId}
          readOnly={props.readOnly}
          attachmentsProcessing={props.attachmentsProcessing}
          amsReconciliationEnabled={props.amsReconciliationEnabled}
        />
      )}
    </AnimateIn>
  );
};

const AttachmentsList = (props: {
  attachments: MessageFile[];
  contextPins: ContextPin[];
  workspaceId?: string;
  readOnly?: boolean;
  attachmentsProcessing?: boolean;
  amsReconciliationEnabled?: boolean;
}) => (
  <div className="flex flex-wrap flex-row gap-1 mt-2 justify-end">
    {props.attachments.map((attachment) => (
      <FileAttachmentChip
        key={attachment.id}
        attachment={attachment}
        workspaceId={props.workspaceId}
        readOnly={props.readOnly}
        processing={props.attachmentsProcessing}
        amsReconciliationEnabled={props.amsReconciliationEnabled}
      />
    ))}
    {props.contextPins.map((pin) => (
      <ContextPinChip key={pin.key} pin={pin} />
    ))}
  </div>
);

const FileAttachmentChip = (props: {
  attachment: MessageFile;
  workspaceId?: string;
  readOnly?: boolean;
  processing?: boolean;
  amsReconciliationEnabled?: boolean;
}) => {
  const [reconciling, setReconciling] = React.useState(false);
  const name = getAttachmentName(props.attachment);

  return (
    <div className={`${CHIP_CLASS} hover:shadow-md transition-shadow duration-150`}>
      <Link
        to={props.attachment.url || ''}
        target="_blank"
        className="flex min-w-0 flex-1 flex-row gap-1.5 hover:opacity-80"
      >
        <PaperClipIcon
          className="size-4 shrink-0 text-neutral-400 dark:text-neutral-500"
          aria-hidden="true"
        />
        <p className="text-xs font-medium text-neutral-600 dark:text-neutral-400 truncate">
          {name}
        </p>
      </Link>
      {props.amsReconciliationEnabled && !props.readOnly && (
        <>
          <button
            type="button"
            onClick={() => setReconciling(true)}
            disabled={props.processing}
            aria-label={
              props.processing ? `Still reading ${name}` : `Reconcile ${name} against AMS`
            }
            title={props.processing ? 'Still reading this file…' : undefined}
            className={
              props.processing
                ? 'shrink-0 cursor-not-allowed text-neutral-400 opacity-50 dark:text-neutral-500'
                : 'shrink-0 text-neutral-400 dark:text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300'
            }
          >
            <CircleStackIcon className="size-4" aria-hidden="true" />
          </button>
          <AmsReconciliationModal
            open={reconciling}
            onClose={() => setReconciling(false)}
            source={{ fileId: props.attachment.id }}
            workspaceId={props.workspaceId}
          />
        </>
      )}
    </div>
  );
};

const ContextPinChip = (props: { pin: ContextPin }) => (
  <div className={CHIP_CLASS} title={props.pin.displayName}>
    {entityTypeIcon(props.pin.entityType, 'size-4 shrink-0 text-neutral-400 dark:text-neutral-500')}
    <p className="text-xs font-medium text-neutral-600 dark:text-neutral-400 truncate">
      {props.pin.displayName}
    </p>
  </div>
);

const getAttachmentName = (attachment: MessageFile) => {
  // Strip the [XYZ] prefix added by the frontend. It adds visual noise.
  return attachment.name.replace(/^\[[^\]]+\] /, '');
};
